/**
 * Frontend-only compliance check: skip synthesize when the selection already
 * fits all budgets. Server trusts passthroughOnly and does not re-check.
 * Keep the 5% tolerance and pool order aligned with referenceAllocator enforcePlan.
 */

function normalizeCategory(raw) {
  if (!raw) return "";
  const m = String(raw).match(/category\s*([a-g])/i);
  if (m) return m[1].toUpperCase();
  return String(raw).trim().toUpperCase().slice(0, 1);
}

function getTrainingCost(training, master) {
  if (training?.cost != null && !Number.isNaN(Number(training.cost))) {
    return Number(training.cost);
  }
  const m = (master || []).find((t) => t?.training_id === training?.training_id);
  return Number(m?.cost ?? 0);
}

function buildCandidates(input) {
  const master = input?.masterTrainingData || [];
  const out = [];
  for (const emp of input?.employees || []) {
    if (!emp?.emp_id) continue;
    for (const t of emp.training_selected || []) {
      if (!t?.training_id) continue;
      const genderRaw = String(emp.gender || "").toLowerCase();
      const disRaw = String(emp.disability ?? "").toLowerCase();
      out.push({
        cost: getTrainingCost(t, master),
        categoryLetter: normalizeCategory(t.training_category),
        race: String(emp.race || "")
          .toLowerCase()
          .trim(),
        gender: genderRaw === "female" ? "female" : genderRaw ? "male" : "",
        disability:
          emp.disability === true ||
          emp.disability === 1 ||
          disRaw === "yes" ||
          disRaw === "true",
      });
    }
  }
  return out;
}

function getDemographicBudget(config, race, gender) {
  const demo = config?.demographics || {};
  const group = demo[race];
  if (!group?.allocation) return Infinity;
  const g = gender === "female" ? "female" : "male";
  return Number(group.allocation[g] ?? 0);
}

function getDisabilityBudget(config) {
  return Number(config?.demographics?.disability?.allocation?.total ?? 0);
}

function getCategoryBudget(config, letter) {
  if (!letter) return Infinity;
  const cat =
    config?.training_categories?.[letter] ||
    config?.training_categories?.[letter.toLowerCase()];
  if (!cat || cat.budget === undefined || cat.budget === null) return Infinity;
  return Number(cat.budget);
}

function poolKey(pool) {
  if (pool.type === "total") return "total";
  if (pool.type === "category") return `cat:${pool.letter}`;
  if (pool.type === "demographic") return `demo:${pool.race}:${pool.gender}`;
  return "disability";
}

function poolTarget(config, pool) {
  if (pool.type === "total") return Number(config?.total_budget ?? 0);
  if (pool.type === "category") return getCategoryBudget(config, pool.letter);
  if (pool.type === "demographic") {
    return getDemographicBudget(config, pool.race, pool.gender);
  }
  return getDisabilityBudget(config);
}

function computePoolCurrents(active) {
  const currents = new Map();
  const add = (key, cost) => currents.set(key, (currents.get(key) ?? 0) + cost);
  for (const a of active) {
    add("total", a.cost);
    if (a.categoryLetter) add(`cat:${a.categoryLetter}`, a.cost);
    if (a.race && a.gender) add(`demo:${a.race}:${a.gender}`, a.cost);
    if (a.disability) add("disability", a.cost);
  }
  return currents;
}

function findActiveViolation(currents, config) {
  const races = ["african", "coloured", "indian", "white", "foreigner"];
  const letters = Object.keys(config?.training_categories || {})
    .map((l) => String(l).toUpperCase())
    .sort();

  for (const r of races) {
    for (const g of ["male", "female"]) {
      const pool = { type: "demographic", race: r, gender: g };
      const target = poolTarget(config, pool);
      const cur = currents.get(poolKey(pool)) || 0;
      if (target === 0 && cur > 0) return pool;
    }
  }

  const disPool = { type: "disability" };
  const disTarget = poolTarget(config, disPool);
  const disCur = currents.get(poolKey(disPool)) || 0;
  if (disTarget === 0 && disCur > 0) return disPool;

  for (const letter of letters) {
    const pool = { type: "category", letter };
    const target = poolTarget(config, pool);
    const cur = currents.get(poolKey(pool)) || 0;
    if (target === 0 && cur > 0) return pool;
  }

  for (const r of races) {
    for (const g of ["male", "female"]) {
      const pool = { type: "demographic", race: r, gender: g };
      const target = poolTarget(config, pool);
      if (target === 0) continue;
      const cur = currents.get(poolKey(pool)) || 0;
      if (cur > target * 1.05) return pool;
    }
  }

  for (const letter of letters) {
    const pool = { type: "category", letter };
    const target = poolTarget(config, pool);
    if (target === 0) continue;
    const cur = currents.get(poolKey(pool)) || 0;
    if (cur > target * 1.05) return pool;
  }

  if (disTarget > 0 && disCur > disTarget * 1.05) return disPool;

  const totalPool = { type: "total" };
  const totalTarget = poolTarget(config, totalPool);
  const totalCur = currents.get(poolKey(totalPool)) || 0;
  if (totalCur > totalTarget * 1.05) return totalPool;

  return null;
}

/**
 * True when every selected training already fits under total, demographic,
 * category, and disability caps (same 5% tolerance as referenceAllocator enforcePlan).
 */
export function isSelectionFullyCompliant(expandedPayload) {
  const config = expandedPayload?.optimizer_config;
  if (!config) return false;
  const active = buildCandidates(expandedPayload);
  const currents = computePoolCurrents(active);
  return findActiveViolation(currents, config) === null;
}
